import React from "react";
import { AuthProvider, useAuth } from "./store/AuthContext";
import { AppProvider, useApp } from "./store/AppContext";
import { useAgents } from "./hooks/useAgents";
import LoginPage from "./components/auth/LoginPage";
import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import MapView from "./components/map/MapView";
import AgentsView from "./components/agents/AgentsView";
import AlertsView, { SendAlertModal } from "./components/alerts/AlertsView";
import AppAlertsView from "./components/alerts/AppAlertsView";
import GeofenceAlertsView from "./components/alerts/GeofenceAlertsView";
import AppUsageView from "./components/usage/AppUsageView";
import AppReleaseView from "./components/releases/AppReleaseView";
import HistoryView from "./components/history/HistoryView";
import ReportsView from "./components/history/ReportsView";
import AgentAttendanceReportView from "./components/reports/AgentAttendanceReportView";
import VisitsMapView from "./components/visits/VisitsMapView";
import ProductsView from "./components/products/ProductsView";
import UsersView from "./components/users/UsersView";
import { Toast } from "./components/shared/UI";
import { T } from "./components/shared/UI";

function Dashboard() {
  const { logout, user } = useAuth();
  const { view, alertModalOpen, alertModalAgent, closeAlertModal, notify, notification, sidebarMobileOpen, closeSidebarMobile } = useApp();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const canManageUsers = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";

  const {
    agents, alerts, zones, loading,
    activeAgents, idleAgents, offlineAgents, sosAgents, activeAlerts,
    createAgent, updateAgent, updatePassword, updateStatus, updateLocationPerm,
    sendAlert, cancelAlert,
  } = useAgents();

  const visibleAgents = agents.filter(a => a.accountStatus !== "off");

  return (
    <div style={styles.app}>
      <Sidebar sosAgents={sosAgents.length} />
      {sidebarMobileOpen && <div className="sidebar-overlay" style={styles.sidebarOverlay} onClick={closeSidebarMobile} />}

      <div style={styles.main}>
        <Header
          activeAgents={activeAgents} idleAgents={idleAgents}
          offlineAgents={offlineAgents} activeAlerts={activeAlerts}
          user={user} onLogout={logout}
        />

        <div style={styles.content}>
          {view === "map" && (
            <MapView agents={visibleAgents} zones={zones} sendAlert={sendAlert} />
          )}
          {view === "agents" && (
            <AgentsView
              agents={visibleAgents} zones={zones}
              createAgent={createAgent}
              updateAgent={updateAgent}
              updatePassword={updatePassword}
              updateStatus={updateStatus}
              updateLocationPerm={updateLocationPerm}
              notify={notify}
            />
          )}
          {view === "alerts" && (
            <AlertsView alerts={alerts} agents={visibleAgents} sendAlert={sendAlert} cancelAlert={cancelAlert} notify={notify} />
          )}
          {view === "history" && (
            <HistoryView agents={visibleAgents} />
          )}
          {view === "reports" && (
            <ReportsView />
          )}
          {view === "visits-map" && (
            <VisitsMapView />
          )}
          {view === "agent-attendance" && (
            <AgentAttendanceReportView />
          )}
          {view === "products" && isSuperAdmin && (
            <ProductsView />
          )}
          {view === "products" && !isSuperAdmin && (
            <div style={styles.denied}>Access denied.</div>
          )}
          {view === "users" && canManageUsers && (
            <UsersView />
          )}
          {view === "users" && !canManageUsers && (
            <div style={styles.denied}>Access denied.</div>
          )}
          {view === "app-alerts" && canManageUsers && (
            <AppAlertsView />
          )}
          {view === "app-alerts" && !canManageUsers && (
            <div style={styles.denied}>Access denied.</div>
          )}
          {view === "geofence-alerts" && canManageUsers && (
            <GeofenceAlertsView />
          )}
          {view === "geofence-alerts" && !canManageUsers && (
            <div style={styles.denied}>Access denied.</div>
          )}
          {view === "app-usage" && canManageUsers && (
            <AppUsageView />
          )}
          {view === "app-usage" && !canManageUsers && (
            <div style={styles.denied}>Access denied.</div>
          )}
          {view === "app-release" && canManageUsers && (
            <AppReleaseView />
          )}
          {view === "app-release" && !canManageUsers && (
            <div style={styles.denied}>Access denied.</div>
          )}
        </div>
      </div>

      <SendAlertModal
        open={alertModalOpen} onClose={closeAlertModal}
        agents={visibleAgents} preAgent={alertModalAgent}
        sendAlert={sendAlert} notify={notify}
      />

      {sosAgents.length > 0 && (
        <div style={styles.sosBanner}>
          🚨 SOS ACTIVE — {sosAgents.map(a => a.name).join(", ")} — RESPOND IMMEDIATELY
        </div>
      )}

      <Toast notification={notification} />
    </div>
  );
}

function Root() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <LoginPage />;
  return <AppProvider><Dashboard /></AppProvider>;
}

export default function App() {
  return <AuthProvider><Root /></AuthProvider>;
}

const styles = {
  app:       { display:"flex", height:"100vh", overflow:"hidden", background:T.bg, color:T.text, fontFamily:T.fontSans, position:"relative" },
  main:      { flex:1, display:"flex", flexDirection:"column", overflow:"hidden" },
  content:   { flex:1, display:"flex", overflow:"hidden" },
  denied:    { flex:1, display:"flex", alignItems:"center", justifyContent:"center", color:T.muted, fontFamily:T.fontMono, fontSize:12 },
  sosBanner: { position:"fixed", top:0, left:0, right:0, background:"rgba(255,68,102,0.96)", color:"#fff", textAlign:"center", padding:"9px 20px", fontSize:13, fontFamily:"'DM Mono',monospace", fontWeight:700, letterSpacing:"1px", zIndex:2000, animation:"sosFlash 1s infinite" },
  sidebarOverlay: { display:"none", position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(4px)", zIndex:999 },
};
